// The top of the admin page: what changed in the last 24 hours and what needs attention
// (briefing.js on the server), plus the Problems panel with the site's recent errors.
import { useCallback, useEffect, useState } from 'react';
import { api } from './api';
import { confirmDialog } from './confirmDialog';

function ago(date) {
  const minutes = Math.round((Date.now() - new Date(date).getTime()) / 60000);
  if (minutes < 60) return minutes <= 1 ? 'just now' : `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h ago`;
  return `${Math.round(hours / 24)} days ago`;
}
const waited = (date) => ago(date).replace(' ago', '');

function Change({ now, before }) {
  if (before === undefined || before === null) return null;
  const diff = now - before;
  if (!diff) return <span className="brief-change flat">same as the day before</span>;
  return <span className={`brief-change ${diff > 0 ? 'up' : 'down'}`}>{diff > 0 ? '▲' : '▼'} {Math.abs(diff)} vs the day before</span>;
}

// "Nov 26" from a YYYY-MM-DD day.
const dayLabel = (key) => new Date(`${key}T12:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
const scrollTo = (id) => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });

export function AdminBriefing({ refreshKey }) {
  const [b, setB] = useState(null);
  const [error, setError] = useState('');
  const load = useCallback(() => api('/admin/briefing').then((r) => { setB(r); setError(''); }).catch((err) => setError(err.message)), []);
  useEffect(() => {
    load();
    const timer = setInterval(load, 60000);
    return () => clearInterval(timer);
  }, [load, refreshKey]);

  if (error) return <p className="error">{error}</p>;
  if (!b) return null;
  const items = [
    ...b.signups.list.map((u) => ({
      key: `s${u.id}`, icon: '👋', tone: 'good',
      text: <><strong>New member: {u.name}</strong>{u.foundingNumber ? ` · founding #${u.foundingNumber}` : ''}{u.source ? ` · came from ${u.source}` : ''} · {ago(u.at)} · {u.trades ? `${u.trades} trades added` : 'no trades yet'}</>,
    })),
    ...b.waiting.map((m) => ({
      key: `w${m.id}`, icon: m.kind === 'Chat' ? '💬' : m.kind === 'Email' ? '📧' : '📩', tone: 'warn',
      text: <><strong>{m.kind} from {m.name}</strong> waiting {waited(m.since)}: “{m.preview}”</>,
      action: { label: 'Open Inbox', go: () => scrollTo('admin-inbox') },
    })),
    ...b.stuck.map((u) => ({
      key: `k${u.id}`, icon: '⏳', tone: 'warn',
      text: <><strong>{u.name}</strong> joined {ago(u.joinedAt)} and hasn't added any trades yet. A short note could help them get started.</>,
      action: { label: 'Email them', href: `mailto:${u.email}?subject=${encodeURIComponent('Need a hand getting started with Market Matrix?')}` },
    })),
    ...b.problems.list.map((p) => ({
      key: `p${p.id}`, icon: '⚠️', tone: 'bad',
      text: <><strong>{p.title}</strong>{p.count > 1 ? ` · ${p.count}×` : ''} · last {ago(p.lastAt)}: {p.message}</>,
      action: { label: 'See problems', go: () => scrollTo('admin-problems') },
    })),
    ...(!b.backup || b.backup.stale ? [{
      key: 'backup', icon: '💾', tone: 'bad',
      text: b.backup ? <><strong>The last database backup was {ago(b.backup.at)}.</strong> The nightly backup may have failed.</> : <><strong>No database backup yet.</strong> Make the first one now.</>,
      action: { label: 'Open Backups', go: () => { window.dispatchEvent(new Event('mm-open-backups')); scrollTo('admin-backups'); } },
    }] : []),
    ...(b.articles.today.length ? [{
      key: 'a-today', icon: '📚', tone: 'plain',
      text: <><strong>Live on the blog today:</strong> {b.articles.today.map((a) => a.title).join(' · ')}</>,
    }] : []),
    ...(b.articles.queue ? [{
      key: 'a-queue', icon: b.articles.queue.low ? '✍️' : '🗂️', tone: b.articles.queue.low ? (b.articles.queue.count ? 'warn' : 'bad') : 'plain',
      text: !b.articles.queue.count
        ? <><strong>No blog articles are scheduled.</strong> Time to write the next batch.</>
        : b.articles.queue.low
          ? <><strong>Blog articles are running low:</strong> {b.articles.queue.count} left, the last one goes live {dayLabel(b.articles.queue.last)} ({b.articles.queue.daysLeft} days). Time to write the next batch.</>
          : <><strong>Blog queue:</strong> {b.articles.queue.count} articles scheduled, one a day through {dayLabel(b.articles.queue.last)}.</>,
    }] : []),
    ...(b.marketCalendar && b.marketCalendar.low ? [{
      key: 'mkt-cal', icon: '📅', tone: 'warn',
      text: <><strong>The market events' report dates run out {dayLabel(b.marketCalendar.until)}.</strong> Ask Claude to add the next official schedules (CPI, jobs, GDP, PCE, Fed).</>,
    }] : []),
    ...(b.marketCalendar && b.marketCalendar.earningsLow ? [{
      key: 'mkt-earn', icon: '📅', tone: 'warn',
      text: <><strong>Earnings dates need the next quarter.</strong> The last big-company report in Market events is {dayLabel(b.marketCalendar.earningsUntil)}. Ask Claude to add the next round as companies announce them.</>,
    }] : []),
    ...(b.articles.tomorrow.length ? [{
      key: 'a-tomorrow', icon: '🗓️', tone: 'plain',
      text: <><strong>Going live tomorrow:</strong> {b.articles.tomorrow.map((a) => a.title).join(' · ')}</>,
    }] : []),
  ];
  const t = b.traffic;
  const tiles = [
    { label: 'New sign-ups', value: b.signups.count, change: <Change now={b.signups.count} before={b.signups.previous} /> },
    { label: 'Visitors', value: t.today.visitors, change: <Change now={t.today.visitors} before={t.yesterday.visitors} /> },
    { label: 'Members active', value: b.activeMembers },
    { label: 'Report cards / demos', value: `${t.today.reportCards} / ${t.today.demos}`, change: <span className="brief-change flat">{t.yesterday.reportCards} / {t.yesterday.demos} the day before</span> },
    { label: 'Messages waiting', value: b.waitingCount, tone: b.waitingCount ? 'warn' : '' },
    { label: 'Problems', value: b.problems.count, tone: b.problems.count ? 'bad' : '' },
  ];
  return (
    <section className="admin-panel admin-briefing" aria-labelledby="briefing-title">
      <div className="admin-panel-header">
        <div>
          <h2 id="briefing-title">Since yesterday</h2>
          <p className="admin-panel-sub">The last 24 hours, and what needs you · updated {ago(b.at)}</p>
        </div>
        <button type="button" className="secondary small" onClick={load}>Refresh</button>
      </div>
      <div className="brief-tiles">
        {tiles.map((x) => (
          <div key={x.label} className={`brief-tile${x.tone ? ` ${x.tone}` : ''}`}>
            <span className="stat-label">{x.label}</span>
            <span className="brief-value">{x.value}</span>
            {x.change}
          </div>
        ))}
      </div>
      <h3 className="brief-needs-title">{items.some((i) => i.tone === 'warn' || i.tone === 'bad') ? 'Needs you' : 'What happened'}</h3>
      {items.length ? (
        <ul className="brief-items">
          {items.map((i) => (
            <li key={i.key} className={`brief-item ${i.tone}`}>
              <span className="brief-icon" aria-hidden="true">{i.icon}</span>
              <span className="brief-text">{i.text}</span>
              {i.action && (i.action.href
                ? <a className="brief-action" href={i.action.href}>{i.action.label} &rarr;</a>
                : <button type="button" className="link-button brief-action" onClick={i.action.go}>{i.action.label} &rarr;</button>)}
            </li>
          ))}
        </ul>
      ) : <p className="empty">All quiet. Nothing needs you right now.</p>}
    </section>
  );
}

export function ProblemsPanel() {
  const [list, setList] = useState(null);
  const [filter, setFilter] = useState('open');
  const [error, setError] = useState('');
  const load = useCallback(() => api('/admin/errors').then(setList).catch((err) => setError(err.message)), []);
  useEffect(() => { load(); }, [load]);

  async function setFixed(p, fixed) {
    try {
      const updated = await api(`/admin/errors/${p._id}`, { method: 'PATCH', body: { fixed } });
      setList((l) => l.map((x) => (x._id === p._id ? updated : x)));
    } catch (err) { setError(err.message); }
  }
  async function remove(p) {
    if (!(await confirmDialog({ title: 'Delete this problem?', message: `“${p.title}” will be removed from the list. If it happens again, it comes back.`, confirmLabel: 'Delete', danger: true }))) return;
    try {
      await api(`/admin/errors/${p._id}`, { method: 'DELETE' });
      setList((l) => l.filter((x) => x._id !== p._id));
    } catch (err) { setError(err.message); }
  }

  const open = (list || []).filter((p) => !p.fixedAt);
  const shown = filter === 'open' ? open : list || [];
  return (
    <section className="admin-panel" id="admin-problems" aria-labelledby="problems-title">
      <div className="admin-panel-header">
        <div>
          <h2 id="problems-title">Problems</h2>
          <p className="admin-panel-sub">Errors on the site in the last 30 days, the same ones sent to Discord · {open.length} open</p>
        </div>
        <div className="inbox-filter">
          <button type="button" className={`secondary small${filter === 'open' ? ' active' : ''}`} onClick={() => setFilter('open')}>Open</button>
          <button type="button" className={`secondary small${filter === 'all' ? ' active' : ''}`} onClick={() => setFilter('all')}>All</button>
        </div>
      </div>
      {error && <p className="error">{error}</p>}
      {!list ? null : shown.length === 0 ? (
        <p className="empty">{filter === 'open' ? 'No open problems. Everything is running fine.' : 'No problems in the last 30 days.'}</p>
      ) : (
        <ul className="inbox-list">
          {shown.map((p) => (
            <li key={p._id} className={`inbox-item problem-item${p.fixedAt ? ' done' : ''}`}>
              <div className="inbox-meta">
                <span className="inbox-category problem-tag">{p.count}×</span>
                <strong>{p.title}</strong>
                {p.fixedAt && <span className="inbox-tag">Fixed</span>}
                <span className="inbox-date">last {ago(p.lastAt)} · first {ago(p.firstAt)}</span>
              </div>
              <p className="inbox-message problem-message">{String(p.message || '').replace(/```/g, '').trim()}</p>
              {p.fields && p.fields.length > 0 && (
                <p className="problem-fields">{p.fields.map((f) => <span key={f.name}><strong>{f.name}:</strong> {f.value}</span>)}</p>
              )}
              <div className="inbox-actions">
                <button type="button" className="secondary small" onClick={() => setFixed(p, !p.fixedAt)}>{p.fixedAt ? 'Mark as open' : 'Mark fixed'}</button>
                <button type="button" className="secondary small inbox-delete" onClick={() => remove(p)}>Delete</button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
